/**
 * CampaignStrategyConfirmationCard Component
 *
 * Interactive card for displaying a campaign strategy overview before
 * full campaign proposal generation. Allows user to review and approve
 * the proposed platforms, budget, target audience, and messaging before
 * committing to full campaign creation.
 *
 * Usage:
 *   <CampaignStrategyConfirmationCard
 *     strategy={campaignStrategy}
 *     campaignName={campaignName}
 *     onApprove={handleApprove}
 *     onRequestChanges={handleRequestChanges}
 *   />
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PlatformLogo, getPlatformName } from '@/components/icons/PlatformLogos';
import type { PlatformKey } from '@/components/icons/PlatformLogos';

export interface PlatformStrategy {
  platform: string;
  rationale: string;
  budget_allocation_percent: number;
  budget_amount: number;
}

export interface TargetAudience {
  age_range: string;
  gender: string;
  interests: string[];
  locations: string[];
  income_level?: string;
}

export interface CampaignStrategy {
  product_summary: string;
  campaign_objective: string;
  platforms: PlatformStrategy[];
  total_budget: number;
  currency: string;
  target_audience: TargetAudience;
  duration_days: number;
  key_messaging: string;
}

export type DestinationChoice = 'website' | 'both' | 'phone';

export interface CampaignStrategyConfirmationCardProps {
  strategy: CampaignStrategy;
  campaignName?: string;
  onApprove: (destinationChoice?: DestinationChoice, confirmedUrl?: string) => void;
  onRequestChanges: (feedback: string) => void;
  disabled?: boolean;
  orgWebsiteUrl?: string | null;
  orgPhone?: string | null;
  orgIndustry?: string | null;
  orgTargetCountries?: string[] | null;
}

const platformLabels: Record<string, string> = {
  meta: 'Meta (Facebook/Instagram)',
  google_ads: 'Google Ads',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  twitter: 'Twitter/X',
  youtube: 'YouTube',
};

const objectiveLabels: Record<string, string> = {
  CONVERSIONS: 'Conversions',
  AWARENESS: 'Brand Awareness',
  TRAFFIC: 'Website Traffic',
  LEAD_GENERATION: 'Lead Generation',
  APP_PROMOTION: 'App Promotion',
  VIDEO_VIEWS: 'Video Views',
  ENGAGEMENT: 'Engagement',
};

export const CampaignStrategyConfirmationCard: React.FC<CampaignStrategyConfirmationCardProps> = ({
  strategy,
  campaignName,
  onApprove,
  onRequestChanges,
  disabled = false,
  orgWebsiteUrl,
  orgPhone,
  orgIndustry,
  orgTargetCountries,
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Destination type: only relevant when BOTH website and phone are set
  const hasBoth = !!orgWebsiteUrl && !!orgPhone;
  const [destinationChoice, setDestinationChoice] = useState<DestinationChoice>('both');

  // Editable URL: pre-filled from org settings, user can correct before approving
  const [confirmedUrl, setConfirmedUrl] = useState(orgWebsiteUrl || '');

  // Case D: neither website nor phone
  const hasNoDestination = !orgWebsiteUrl && !orgPhone;

  const handleRequestChanges = () => {
    if (feedback.trim()) {
      onRequestChanges(feedback.trim());
      setFeedback('');
      setShowFeedback(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (disabled) {
    return null;
  }

  return (
    <Card variant="outlined" padding="none" className="overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-emerald-500/5 to-blue-500/5 border-b border-line dark:border-border-dark">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-ink flex items-center gap-2">
              <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Campaign Strategy Review
            </h3>
            <p className="text-sm text-ink-2 mt-1">
              Review the proposed strategy below. Approve to generate the full campaign, or request changes.
            </p>
            {campaignName && (
              <p className="text-sm font-medium text-ok mt-2">
                {campaignName}
              </p>
            )}
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-ok-soft text-emerald-800 border border-emerald-200">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Awaiting Your Approval
          </span>
        </div>
      </div>

      {/* Strategy Content */}
      <div className="p-6 space-y-6">

        {/* Product Summary */}
        <div>
          <h4 className="text-sm font-semibold text-ink-3 uppercase tracking-wider mb-2">
            Product / Service
          </h4>
          <p className="text-ink">
            {strategy.product_summary}
          </p>
        </div>

        {/* Business Context */}
        {hasNoDestination ? (
          /* Case D: neither website nor phone — blocking warning */
          <div className="p-4 bg-bad-soft border border-bad/40 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-bad flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-bad mb-1">
                  No Campaign Destination Set
                </h4>
                <p className="text-sm text-bad">
                  Your business profile has no website or phone number. Campaigns need a destination for users to reach you. Add one in Organization Settings before approving.
                </p>
                <a
                  href="/settings?tab=organizations"
                  className="inline-block mt-2 text-sm font-medium text-bad underline underline-offset-2 hover:text-red-900"
                >
                  Go to Settings →
                </a>
              </div>
            </div>
          </div>
        ) : (
          /* Cases A / B / C: at least one destination set */
          <div className="p-4 bg-work-soft border border-work/30 rounded-lg space-y-3">
            <h4 className="text-sm font-semibold text-work uppercase tracking-wider">
              Business Context
            </h4>

            {/* Fields grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {orgIndustry && (
                <div className="flex items-start gap-2">
                  <span className="text-primary font-medium min-w-[72px]">Industry:</span>
                  <span className="text-ink">{orgIndustry}</span>
                </div>
              )}
              {orgTargetCountries && orgTargetCountries.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-primary font-medium min-w-[72px]">Markets:</span>
                  <span className="text-ink">{orgTargetCountries.join(', ')}</span>
                </div>
              )}
              <div className="flex items-start gap-2 sm:col-span-2">
                <span className="text-primary font-medium min-w-[72px] pt-1.5">Website:</span>
                {orgWebsiteUrl ? (
                  <div className="flex-1">
                    <input
                      type="url"
                      value={confirmedUrl}
                      onChange={(e) => setConfirmedUrl(e.target.value)}
                      placeholder="https://yoursite.com"
                      className="w-full px-2.5 py-1.5 text-sm rounded border border-work/40 bg-card dark:bg-card-darkAlt text-ink focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-primary mt-1">
                      This exact URL will be used for all ads. Correct it if needed (e.g. add a specific landing page).
                    </p>
                  </div>
                ) : (
                  <span className="text-warn italic">Not set — lead gen campaigns will be proposed</span>
                )}
              </div>
              <div className="flex items-start gap-2">
                <span className="text-primary font-medium min-w-[72px]">Phone:</span>
                {orgPhone
                  ? <span className="text-ink">
                      {orgPhone.slice(0, orgPhone.length - 4).replace(/\d/g, '•') + orgPhone.slice(-4)}
                    </span>
                  : <span className="text-ink-3 italic">Not set</span>
                }
              </div>
            </div>

            {/* Case C: both set — destination picker */}
            {hasBoth && (
              <div className="pt-2 border-t border-work/30">
                <p className="text-sm font-medium text-work mb-2">
                  Campaign destination type:
                </p>
                <div className="space-y-1.5">
                  {([
                    { value: 'both', label: 'Website + WhatsApp/Phone', desc: 'Include both website traffic campaigns and WhatsApp/lead gen ads', recommended: true },
                    { value: 'website', label: 'Website only', desc: 'All campaigns drive traffic to your website' },
                    { value: 'phone', label: 'WhatsApp / lead gen only', desc: 'All campaigns use click-to-chat or lead form objectives' },
                  ] as { value: DestinationChoice; label: string; desc: string; recommended?: boolean }[]).map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${
                        destinationChoice === opt.value
                          ? 'border-work-dot bg-work-soft'
                          : 'border-work/30 hover:bg-work-soft/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="destinationChoice"
                        value={opt.value}
                        checked={destinationChoice === opt.value}
                        onChange={() => setDestinationChoice(opt.value)}
                        className="mt-0.5 accent-blue-600"
                      />
                      <div>
                        <span className="text-sm font-medium text-ink">
                          {opt.label}
                          {opt.recommended && (
                            <span className="ml-2 text-xs font-normal text-primary">(recommended)</span>
                          )}
                        </span>
                        <p className="text-xs text-ink-3 mt-0.5">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-primary">
              Update in Settings → Organizations if needed before generating the campaign.
            </p>
          </div>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-ground dark:bg-card-darkAlt rounded-lg p-3 text-center">
            <p className="text-xs text-ink-3 uppercase tracking-wider">Total Budget</p>
            <p className="text-lg font-bold text-ink mt-1">
              {formatCurrency(strategy.total_budget, strategy.currency)}
            </p>
          </div>
          <div className="bg-ground dark:bg-card-darkAlt rounded-lg p-3 text-center">
            <p className="text-xs text-ink-3 uppercase tracking-wider">Duration</p>
            <p className="text-lg font-bold text-ink mt-1">
              {strategy.duration_days} days
            </p>
          </div>
          <div className="bg-ground dark:bg-card-darkAlt rounded-lg p-3 text-center">
            <p className="text-xs text-ink-3 uppercase tracking-wider">Platforms</p>
            <p className="text-lg font-bold text-ink mt-1">
              {strategy.platforms.length}
            </p>
          </div>
          <div className="bg-ground dark:bg-card-darkAlt rounded-lg p-3 text-center">
            <p className="text-xs text-ink-3 uppercase tracking-wider">Objective</p>
            <p className="text-lg font-bold text-ink mt-1">
              {objectiveLabels[strategy.campaign_objective] || strategy.campaign_objective}
            </p>
          </div>
        </div>

        {/* Platform Breakdown */}
        <div>
          <h4 className="text-sm font-semibold text-ink-3 uppercase tracking-wider mb-3">
            Platform Strategy & Budget Allocation
          </h4>
          <div className="space-y-3">
            {strategy.platforms.map((platform, index) => (
              <div
                key={index}
                className="rounded-lg border border-line dark:border-border-dark bg-card dark:bg-card-darkAlt p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <PlatformLogo platform={platform.platform as PlatformKey} size="sm" />
                      <h5 className="font-semibold text-ink">
                        {getPlatformName(platform.platform) || platformLabels[platform.platform] || platform.platform}
                      </h5>
                    </div>
                    <p className="text-sm text-ink-2 mt-1">
                      {platform.rationale}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-lg font-bold text-ink">
                      {formatCurrency(platform.budget_amount, strategy.currency)}
                    </p>
                    <p className="text-xs text-ink-3">
                      {platform.budget_allocation_percent}% of budget
                    </p>
                  </div>
                </div>
                {/* Budget Bar */}
                <div className="mt-3">
                  <div className="w-full bg-inset rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(platform.budget_allocation_percent, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Target Audience */}
        <div>
          <h4 className="text-sm font-semibold text-ink-3 uppercase tracking-wider mb-3">
            Target Audience
          </h4>
          <div className="rounded-lg border border-line dark:border-border-dark bg-card dark:bg-card-darkAlt p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-ink-3 uppercase tracking-wider">Age Range</p>
                <p className="text-sm font-medium text-ink mt-0.5">
                  {strategy.target_audience.age_range}
                </p>
              </div>
              <div>
                <p className="text-xs text-ink-3 uppercase tracking-wider">Gender</p>
                <p className="text-sm font-medium text-ink mt-0.5 capitalize">
                  {strategy.target_audience.gender}
                </p>
              </div>
              <div>
                <p className="text-xs text-ink-3 uppercase tracking-wider">Locations</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {strategy.target_audience.locations.map((loc, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-work-soft text-work"
                    >
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
              {strategy.target_audience.income_level && (
                <div>
                  <p className="text-xs text-ink-3 uppercase tracking-wider">Income Level</p>
                  <p className="text-sm font-medium text-ink mt-0.5 capitalize">
                    {strategy.target_audience.income_level}
                  </p>
                </div>
              )}
            </div>

            {/* Interests */}
            <div className="mt-4">
              <p className="text-xs text-ink-3 uppercase tracking-wider mb-1">Interests</p>
              <div className="flex flex-wrap gap-1.5">
                {(strategy.target_audience.interests ?? []).map((interest, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-inset text-ink-2"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Key Messaging */}
        <div>
          <h4 className="text-sm font-semibold text-ink-3 uppercase tracking-wider mb-2">
            Key Messaging Approach
          </h4>
          <div className="rounded-lg border border-line dark:border-border-dark bg-card dark:bg-card-darkAlt p-4">
            <p className="text-sm text-ink-2 italic">
              "{strategy.key_messaging}"
            </p>
          </div>
        </div>

        {/* Feedback Input (shown when "Request Changes" is clicked) */}
        {showFeedback && (
          <div className="rounded-lg border-2 border-warn/30 bg-warn-soft p-4">
            <label className="block text-sm font-medium text-ink-2 mb-2">
              What would you like to change?
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g., Increase the budget to $10,000, add TikTok, target a younger audience (18-25), focus on US only..."
              className="w-full rounded-lg border border-line-2 dark:border-border-dark bg-card dark:bg-card-darkAlt text-ink px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowFeedback(false);
                  setFeedback('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleRequestChanges}
                disabled={!feedback.trim()}
              >
                Submit Changes
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      <div className="p-6 bg-ground border-t border-line dark:border-border-dark">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          {/* Summary */}
          <div className="text-sm text-ink-2">
            <span>
              {formatCurrency(strategy.total_budget, strategy.currency)} across{' '}
              <strong>{strategy.platforms.length}</strong> platform{strategy.platforms.length !== 1 ? 's' : ''} for{' '}
              <strong>{strategy.duration_days}</strong> days
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!showFeedback && (
              <Button
                variant="outline"
                onClick={() => setShowFeedback(true)}
                disabled={hasNoDestination}
              >
                Request Changes
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => onApprove(hasBoth ? destinationChoice : undefined, confirmedUrl.trim() || undefined)}
              disabled={hasNoDestination}
              title={hasNoDestination ? 'Add a website or phone number in Organization Settings first' : undefined}
              icon={
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              }
            >
              Approve & Create Campaign
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CampaignStrategyConfirmationCard;
